import type { PreviewUploadsController } from '../../../../hooks/useDappFileHandlers';
import { BasicDetailsSectionBase } from '../../../dapp-form/sections/BasicDetailsSectionBase';
import { MediaUploadWithExisting } from '../../MediaUploadWithExisting';
import { PreviewUploadsField } from '../components/PreviewUploadsField';
import { PackageNameField } from '../components/PackageNameField';
import type { DashboardDetailsInputChangeEvent, FormState } from '../types';

interface BasicDetailsSectionProps {
  formData: FormState;
  previewUploads: PreviewUploadsController;
  resolvedDappIconUrl: string | null;
  resolvedBannerUrl: string | null;
  resolvedPreviewUrls: (string | null)[];
  existingPreviewUrls: (string | null)[];
  onInputChange: (event: DashboardDetailsInputChangeEvent) => void;
  onSingleFileChange: (
    fieldName: 'dappIcon' | 'banner' | 'editorsChoiceGraphic',
    file: File | null,
  ) => Promise<void>;
  onRemoveExistingDappIcon: () => void;
  onRemoveExistingBanner: () => void;
  onRemoveExistingPreview: (index: number) => void;
}

export function BasicDetailsSection({
  formData,
  previewUploads,
  resolvedDappIconUrl,
  resolvedBannerUrl,
  resolvedPreviewUrls,
  existingPreviewUrls,
  onInputChange,
  onSingleFileChange,
  onRemoveExistingDappIcon,
  onRemoveExistingBanner,
  onRemoveExistingPreview,
}: BasicDetailsSectionProps) {
  return (
    <BasicDetailsSectionBase
      dappName={formData.dappName}
      subtitle={formData.subtitle}
      description={formData.description}
      releaseApprovalMode={formData.releaseApprovalMode}
      onInputChange={onInputChange}
      packageNameField={<PackageNameField value={formData.packageName} />}
      uploadFields={
        <>
          <MediaUploadWithExisting
            name="dappIcon"
            label="dApp Icon"
            required
            description="dApp icons must contain a background color (no transparency)."
            dimensions="512x512px"
            value={formData.dappIcon}
            existingUrl={resolvedDappIconUrl}
            onChange={(file: File | null) =>
              onSingleFileChange('dappIcon', file)
            }
            onRemoveExisting={onRemoveExistingDappIcon}
            buttonType="upload"
            placeholderClassName="size-[90px]"
          />
          <MediaUploadWithExisting
            name="banner"
            label="Banner"
            required
            description="This appears at the top of the detail page, behind the dApp icon."
            dimensions="1200x600px"
            value={formData.banner}
            existingUrl={resolvedBannerUrl}
            onChange={(file: File | null) => onSingleFileChange('banner', file)}
            onRemoveExisting={onRemoveExistingBanner}
            buttonType="add"
            placeholderClassName="h-[90px] w-[180px]"
          />
        </>
      }
      previewUploadsField={
        <PreviewUploadsField
          dappPreviews={formData.dappPreviews}
          previewUploads={previewUploads}
          existingPreviewUrls={existingPreviewUrls}
          resolvedPreviewUrls={resolvedPreviewUrls}
          onRemoveExistingPreview={onRemoveExistingPreview}
        />
      }
    />
  );
}
